import Vue from 'vue'
import { format, formatRelative, formatDistanceToNow, addDays } from 'date-fns'
import { locales } from './locales'

const dateFnsLoaders = {
  fr: () =>
    import(/* webpackChunkName: "date-fns-locale-fr" */ 'date-fns/locale/fr'),
  it: () =>
    import(/* webpackChunkName: "date-fns-locale-it" */ 'date-fns/locale/it'),
  es: () =>
    import(/* webpackChunkName: "date-fns-locale-es" */ 'date-fns/locale/es'),
  de: () =>
    import(/* webpackChunkName: "date-fns-locale-de" */ 'date-fns/locale/de'),
  pt: () =>
    import(/* webpackChunkName: "date-fns-locale-pt" */ 'date-fns/locale/pt'),
  en: () =>
    import(
      /* webpackChunkName: "date-fns-locale-en" */ 'date-fns/locale/en-GB'
    ),
  ja: () =>
    import(/* webpackChunkName: "date-fns-locale-ja" */ 'date-fns/locale/ja'),
  nl: () =>
    import(/* webpackChunkName: "date-fns-locale-nl" */ 'date-fns/locale/nl'),
  pl: () =>
    import(/* webpackChunkName: "date-fns-locale-pl" */ 'date-fns/locale/pl'),
  sv: () =>
    import(/* webpackChunkName: "date-fns-locale-sv" */ 'date-fns/locale/sv'),
  cs: () =>
    import(/* webpackChunkName: "date-fns-locale-cs" */ 'date-fns/locale/cs'),
  ro: () =>
    import(/* webpackChunkName: "date-fns-locale-ro" */ 'date-fns/locale/ro'),
  da: () =>
    import(/* webpackChunkName: "date-fns-locale-da" */ 'date-fns/locale/da'),
  el: () =>
    import(/* webpackChunkName: "date-fns-locale-el" */ 'date-fns/locale/el'),
  fi: () =>
    import(/* webpackChunkName: "date-fns-locale-fi" */ 'date-fns/locale/fi'),
  no: () =>
    import(/* webpackChunkName: "date-fns-locale-nb" */ 'date-fns/locale/nb'),
}

const localesDateFns = {}

async function loadDateFnsLocale(code) {
  if (localesDateFns[code] || !dateFnsLoaders[code]) return

  localesDateFns[code] = (await dateFnsLoaders[code]()).default
}

export default async function ({ app }) {
  const currentCode = () => app.i18n?.localeProperties?.code || 'fr'
  const dateFnsLocale = () => localesDateFns[currentCode()]

  await loadDateFnsLocale(currentCode())

  app.i18n.onLanguageSwitched = (oldLocale, newLocale) =>
    loadDateFnsLocale(newLocale)

  Vue.filter('dateRelative', function (value) {
    if (!value) return ''
    value = value.toString()
    return formatRelative(new Date(value), new Date(), {
      locale: dateFnsLocale(),
    })
  })

  Vue.filter('dateAgo', function (value) {
    if (!value) return ''
    value = value.toString()
    return formatDistanceToNow(new Date(value), {
      addSuffix: true,
      locale: dateFnsLocale(),
    })
  })

  Vue.filter('dateFormat', function (value, outputFormat) {
    if (!value) return ''
    value = value.toString()
    return format(new Date(value), outputFormat, {
      locale: dateFnsLocale(),
    })
  })

  Vue.filter('addDaysToToday', function (days, outputFormat) {
    return format(addDays(new Date(), days), outputFormat, {
      locale: dateFnsLocale(),
    })
  })

  Vue.filter('booleanFormat', function (value) {
    return value ? 'Oui' : 'Non'
  })

  Vue.filter('numberFormat', function (value, outputFormat) {
    if (typeof value !== 'number') {
      return value
    }
    let formatter = new Intl.NumberFormat(outputFormat)
    return formatter.format(value)
  })

  Vue.filter('priceFormatCompact', function (value) {
    if (typeof value !== 'number') {
      return value
    }

    const locale = locales.find(l => l.code === app.i18n.localeProperties.code)
    const fractionDigits = value % 1 === 0 ? 0 : 2

    try {
      return new Intl.NumberFormat(locale.iso, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: fractionDigits,
      }).format(value)
    } catch (e) {
      return value.toFixed(fractionDigits) + ' €'
    }
  })

  Vue.filter('priceFormat', function (value) {
    if (typeof value !== 'number') {
      return value
    }

    const locale = locales.find(l => l.code === app.i18n.localeProperties.code)

    try {
      return new Intl.NumberFormat(locale.iso, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
      }).format(value)
    } catch (e) {
      return value.toFixed(2) + ' €'
    }
  })
}
